import { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { colors } from "brand";
import { TableSkeleton } from "common-fe";
import { useTranslation } from "i18n-fe";
import { DERIVED_RIDE_STATUSES } from "score-common";
import { useClassResults, RESULTS_POLL_MS } from "../hooks/useClassResults";
import {
  buildClassResults,
  isClassResultsFinal,
  isClassResultsRevealed,
  groupResultsByDivision,
  RESULT_STATUS,
} from "../utils/orderOfGo";
import { formatComposite } from "../utils/formatComposite";

// #644 — the class-level results view (docs/score/announcer.md, Screens #4 /
// Decision #2): "possibly the announcer reads placings, but the information
// must be available either way." Sits directly under each ClassHeader so the
// full placing-ordered list for that class is somewhere the announcer can just
// look, no extra tap, once every ride in the class is FINAL.
//
// The CURRENT/live class polls (RESULTS_POLL_MS, ~15s); so does any class
// that has actually STARTED scoring and isn't final yet, regardless of ring
// position — #919: "isLive" is CURRENT's class, and CURRENT moves off a ride
// the instant its marks are confirmed (that ride leaves IN_PROGRESS, see
// OrderOfGo.jsx). Stopping this class's poll the moment it loses "live" — on
// the ORIGINAL assumption that "no longer live" meant "final, won't change
// again" — froze the just-confirmed ride's composite/judge-% at whatever one
// last race-y fetch caught (a stale PARTIAL, or a status snapshot
// mid-write), which is what read as marks vanishing/reverting to "not
// placed". Keep polling until this class's OWN results are actually final,
// not merely until it stops being the ring's live class.
//
// #2147 — a class nobody has reached YET (never live, no scoring activity)
// does NOT poll at all: it fetches once and waits on the ring-scoped
// class-results-summary call (OrderOfGo's `placed` prop) to say when it's
// worth asking again. Before #2147 this component polled EVERY non-final
// class in the ring, all day, which is the N-poller-per-ring bug
// docs/score/announcer-ui-redesign.md §10 Gap 1 describes.
//
// ⚠️ Same dark-mode trap as RideRow: brand `primary.main` is dark grey and
// disappears on a dark background, so emphasis here is colors.foxOrange /
// text.* only.

// #2634 — one placing row, factored out of the flat/grouped render paths
// below so grouping by division (groupResultsByDivision, orderOfGo.js) is
// purely a partition of this same row markup, never a second copy of it.
function ResultRow({ row, t }) {
  const rowConfirmed = row.status === RESULT_STATUS.CONFIRMED;
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      data-testid="class-results-row"
      data-ride-id={row.rideId}
      data-placing={row.placing ?? undefined}
      data-status={row.status || undefined}
    >
      <Typography
        variant="h6"
        fontWeight={800}
        sx={{ minWidth: 48, flexShrink: 0, color: row.placing ? colors.foxOrange : "text.secondary" }}
      >
        {row.placing || t("results.unplaced")}
      </Typography>
      <Typography sx={{ minWidth: 56, flexShrink: 0 }} color="text.secondary">
        {row.competitorNumber || "—"}
      </Typography>
      {/* #2583 (TRI-236) — this box, not the composite below, is the
          one that must give way when the row is narrower than the
          sum of its parts. `minWidth: 160` here (its old value)
          forced a floor the 398px lookup band (LOOKUP_BAND_WIDTH,
          frame.js) can't always afford once the fixed
          placing/competitor columns and the row's own gaps are
          subtracted — the composite typography below has no
          minWidth of its own, so with default flex-shrink:1 on
          every item, the row's total *minimum* content width
          (driven by this box's 160px floor, since unbreakable
          score text can't shrink smaller than itself) exceeded the
          band width. That overflow doesn't wrap or scroll into
          view: the ancestor `frame-band-lookup`/`lookup-placings`
          boxes set `overflowY: "auto"` and nothing for
          overflow-x, and per the CSS overflow spec a box can't mix
          "visible" on one axis with "auto"/"hidden" on the other —
          the browser silently promotes the unset overflow-x to
          "auto" too, which clips (not wraps) whatever sits past
          the right edge. The composite score is the last item in
          the row, so it's the one whose right edge (often just the
          trailing "%") lands past that edge and gets clipped. Fix:
          let this box shrink all the way to 0 (matching
          LookupColumn's own RideListRow name box, which already
          uses `minWidth: 0` and never exhibited this bug) so the
          rider/horse names give up space first; the composite gets
          `flexShrink: 0` just below so it is never the one asked
          to give up space at all. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" fontWeight={700}>
          {row.riderName || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {row.horseName || "—"}
        </Typography>
      </Box>
      {/* #2183 AC5 — a DSHB place-only ride's composite is
          genuinely null (no score box on the sheet at all, DR211.1/
          DR210.5), never a fabricated 0. formatComposite already
          turns null into "" rather than "0.000" (see its own
          comment), but rendering "{{value}}%" with an empty value
          would still read as a bare "%" — an artifact of its own.
          Render nothing for that class instead: DR211.5 requires
          the PLACING (shown just left of this) to publish, not an
          invented percentage. */}
      {Number.isFinite(row.composite) ? (
        <Typography
          variant="h6"
          fontWeight={700}
          data-testid="class-results-composite"
          sx={{ flexShrink: 0, color: rowConfirmed ? "success.main" : "text.primary" }}
        >
          {t("results.composite", { value: formatComposite(row.composite) })}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default function ClassResults({
  classId,
  isLive,
  placed = false,
  rideRows,
  onResults,
  online = true,
  statusesUnknown = false,
}) {
  const { t } = useTranslation("announcer");
  const [everFinal, setEverFinal] = useState(false);
  // #2147 regression fix: latched sticky flag for `hasResultsActivity` below
  // (same one-render-lagged pattern as `everFinal` itself, and for the same
  // reason — the gate that reads it feeds `useClassResults`' `intervalMs`
  // BEFORE that hook call runs, so it cannot read the freshly-fetched
  // `results` from THIS render's own hook call; it reads what a PRIOR
  // render's fetch already committed to state instead). Sticky/monotonic is
  // fine, unlike a plain re-derivation every render: once real results
  // activity is ever observed, this class deserves to keep polling until
  // `everFinal` stops it, matching #919's guarantee below.
  const [hasResultsActivity, setHasResultsActivity] = useState(false);

  // #2147 — hoisted ahead of useClassResults so it can gate the poll below
  // (docs/score/announcer-ui-redesign.md §10 Gap 1: a class nobody has
  // reached yet must not poll its own results forever). Mirrors the
  // `classStarted` predicate further down (kept in sync with it there, minus
  // `resultsUnknown`, which is derived FROM this fetch and so can't gate its
  // own trigger) — at least one of this class's own rides has moved off
  // PENDING/SCRATCHED, i.e. scoring has genuinely begun on it.
  const hasScoringActivity =
    statusesUnknown ||
    (rideRows || []).some(
      (row) =>
        row?.status &&
        row.status !== DERIVED_RIDE_STATUSES.PENDING &&
        row.status !== DERIVED_RIDE_STATUSES.SCRATCHED,
    );

  // #2147 — the per-class continuous poll (RESULTS_POLL_MS) now runs only
  // for the CURRENT/live class, or a class that has genuinely started
  // scoring and whose own results aren't final yet. Before #2147 this was
  // `isLive || !everFinal`, which polled EVERY not-yet-final class in the
  // ring forever — including classes nobody has reached yet, the N-poller
  // bug docs/score/announcer-ui-redesign.md §10 Gap 1 describes (a 20-class
  // ring meant 20 requests every RESULTS_POLL_MS). `hasScoringActivity` (or
  // its results-derived sibling `hasResultsActivity` above) — no
  // `everFinal`-adjacent `placed` term — is what closes that gap — it is
  // false for a class nobody has touched, so its poll never turns on in the
  // first place, no `placed` check needed. `placed` (the cheap ring-wide
  // class-results-summary call, OrderOfGo's prop) is DELIBERATELY NOT part
  // of this gate: `placed` is a strictly weaker stop condition than
  // `everFinal` (placings can be committed — `setClassPlacings` only
  // requires CONFIRMED/FINAL, results.js's isReleasableStatus — while a
  // judge is still writing comments on another sheet in the same class), so
  // gating the poll on `!placed` stopped it one refresh deep on the
  // false->true `placed` transition below and never restarted it if that
  // one refresh landed on a still-not-final response. `everFinal` alone
  // still holds #919's guarantee: a class that just lost "live" keeps
  // polling until ITS OWN results are final, independent of ring position
  // or the summary's coarser `placed` reading.
  //
  // #2147 regression fix (a full Chrome run surfaced this against #1647's
  // three "reveal never regresses" specs) — `hasScoringActivity` above reads
  // the RIDE-level status batch poll (OrderOfGo's day-sheet feed), which is a
  // DIFFERENT signal than this class's own results fetch: a ride can already
  // carry CONFIRMED/FINAL results (real sheets, judges done) while the
  // day-sheet batch poll still reports it PENDING for that ride (never
  // transitioned through IN_PROGRESS at all — the same day-sheet/score-system
  // split #386's manual-ride fix and this card's own `statusesUnknown` OR
  // already account for elsewhere). Without `hasResultsActivity` in this OR,
  // a class whose OWN results are already CONFIRMED-but-not-final, fetched
  // once in fetch-once mode because `hasScoringActivity` never saw ride-level
  // activity, never polled again — freezing its composite/placings at that
  // first snapshot forever, which is exactly #919's guarantee (keep polling
  // until THIS class's own results are final) breaking for that one channel.
  // `results` alone can't stand in for the WHOLE gate (score-api's
  // buildRideResult defaults an utterly untouched ride to PARTIAL too — see
  // results.js's status ladder — so PARTIAL is not evidence either way), but
  // CONFIRMED/FINAL are: neither is reachable without a real, confirmed
  // sheet. See the `hasResultsActivity` effect below for why it is a sticky
  // state flag rather than derived inline here, same as `everFinal`.
  const shouldPollDetail =
    isLive || (!everFinal && (hasScoringActivity || hasResultsActivity));
  const { results, error, refresh } = useClassResults(classId, {
    intervalMs: shouldPollDetail ? RESULTS_POLL_MS : undefined,
    online, // #705 — offline falls back to last-cached placings/composites
  });

  useEffect(() => {
    if (isClassResultsFinal(results)) setEverFinal(true);
  }, [results]);

  // #2147 regression fix — sets the sticky `hasResultsActivity` flag read by
  // `shouldPollDetail` above. Runs AFTER `results` is fetched, so it can only
  // ever affect the NEXT render's poll decision, one tick behind (the same
  // lag `everFinal`'s effect just above already accepts) — `results` is not
  // in scope yet at the point `shouldPollDetail` is computed, since it comes
  // back FROM the `useClassResults` call `shouldPollDetail` itself feeds.
  useEffect(() => {
    if (
      (results || []).some(
        (r) => r?.status === RESULT_STATUS.CONFIRMED || r?.status === RESULT_STATUS.FINAL,
      )
    ) {
      setHasResultsActivity(true);
    }
  }, [results]);

  // #2147 — a class that never showed "scoring activity" through the ride-
  // status batch poll (e.g. every ride resolved straight to SCRATCHED, which
  // `hasScoringActivity` deliberately excludes, matching score-api's
  // isClassPlaced) would otherwise never re-fetch once `placed` flips true,
  // since `shouldPollDetail` above never turned on for it. One manual
  // refresh on a LATER false->true transition (skipped while already
  // polling live, which will pick it up on its own next tick) closes that
  // gap without adding a poller. The ref starts at `null`, not `false`, so
  // a class that is ALREADY placed at mount does not fire this a second
  // time on top of useClassResults' own built-in fetch-once-on-mount — only
  // a transition witnessed AFTER mount counts.
  const wasPlacedRef = useRef(null);
  useEffect(() => {
    if (wasPlacedRef.current === null) {
      wasPlacedRef.current = placed;
      return;
    }
    if (placed && !wasPlacedRef.current && !isLive) refresh();
    wasPlacedRef.current = placed;
  }, [placed, isLive, refresh]);

  // Lift the raw results up to OrderOfGo so it can badge each RideRow's
  // AccordionSummary with its placing — the class-level list below is not the
  // only place a placing shows up (docs/score/announcer.md: "a single ride's
  // badge alone doesn't show ORDER", so both views exist together).
  useEffect(() => {
    onResults?.(classId, results);
  }, [classId, results, onResults]);

  // #714 — still loading the first response: a small skeleton instead of a
  // blank flash (never a stale/empty state, per the original comment).
  if (results === null) {
    return (
      <Box
        data-testid="class-results-loading"
        data-class-id={classId}
        sx={{ mt: 1, mb: 1, p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <TableSkeleton rows={2} dense showHeader={false} />
      </Box>
    );
  }

  // #920 — a SCRATCHED ride was never judged and has no placing/composite to
  // show; list it in the reveal-gate math (isClassResultsFinal/Revealed
  // above already exclude it from their every() checks) but not as a row
  // here — there's nothing to read out for a horse that didn't compete.
  const rows = buildClassResults(results, rideRows).filter(
    (row) => row.status !== RESULT_STATUS.SCRATCHED
  );
  // #2634 — null when the class doesn't need dividing (no division anywhere,
  // or every row folds to the same single division): the render below falls
  // straight back to today's flat list, byte-for-byte, for that case (AC2).
  const divisionGroups = groupResultsByDivision(rows);
  const final = isClassResultsFinal(results);
  // #699 — scores are readable once every ride is CONFIRMED (all judges
  // confirmed their marks), not only once every ride is signed FINAL. Rides
  // that are CONFIRMED-but-not-FINAL show their composite in green ("ready to
  // announce") with no placing yet; FINAL rows keep the orange placing.
  const revealed = isClassResultsRevealed(results);
  // #2082 — "Results not available yet — scoring in progress." was showing
  // on classes that are not being scored at all, which reads as "we're
  // working on it" when nothing is happening. Gate the message on the class
  // actually having at least one scoresheet: at least one of THIS class's
  // rides carries a status that is neither PENDING (never touched) nor
  // SCRATCHED (never judged, #920 — a scratch has no scoresheet either).
  //
  // Deliberately NOT score-common's deriveClassStatus (ride-queue.js,
  // #1280): that helper checks isClassComplete FIRST, which treats an
  // ALL-SCRATCHED class as COMPLETE rather than SCHEDULED — so
  // `deriveClassStatus(rideRows) !== SCHEDULED` reads a class with literally
  // zero scoresheets (every ride scratched, none ever scored) as "started",
  // putting the false "scoring in progress" claim right back on screen for
  // exactly the case #920 already established reaches this component.
  // isClassComplete's COMPLETE-on-all-scratched is the right call for ITS
  // own callers (closing a ring queue with nothing left to show), but wrong
  // for this question, so the underlying `started` predicate is inlined
  // here instead of reusing the composed helper.
  //
  // This does not touch the #699 anti-leak gate above: `revealed` (and
  // therefore withholding scores from a class that IS being scored) is
  // completely unchanged, this only decides which UNREVEALED copy to show.
  //
  // #2086 — that inlined predicate reads false when the results fetch itself
  // FAILED with no cache, because no ride carries a real status yet either:
  // "we don't know" and "we know it's zero" both looked like "not started".
  // useClassResults only sets `error` on that exact no-cache-failure branch
  // (see its #2073 comment) — a successful fetch and a cache fallback both
  // clear it — so it is the explicit signal for "no information", kept
  // separate from `classStarted`'s "known zero scoresheets" below rather
  // than folded into the same boolean incidentally.
  const resultsUnknown = Boolean(error);
  // #2087 — the OTHER fetch that feeds this predicate can be just as unknown:
  // `row.status` above comes from the batch ride-status poll (OrderOfGo.jsx),
  // not from `results`, and normalizeStatus (orderOfGo.js) defaults every ride
  // to PENDING while that poll has never answered (or is failing
  // persistently). Without this OR, an actively-scored class reads as
  // "hasn't started" for as long as that poll stays unanswered — the same
  // false-positive shape #2086 closed for the results fetch, reachable
  // through the other channel. `statusesUnknown` comes from OrderOfGo.jsx's
  // `statusesLastUpdated == null`, which flips to known-false the first time
  // the poll actually lands, so #2082's genuine zero-scoresheet classes (all
  // PENDING/SCRATCHED, statuses answered) are unaffected.
  // #2147 — reuses `hasScoringActivity` (hoisted above, ahead of the
  // useClassResults call it also gates) instead of re-deriving the same
  // rideRows/statusesUnknown check a second time; only `resultsUnknown` is
  // added back in here; it depends on this component's own fetch, so it
  // isn't available yet at the point `hasScoringActivity` is computed.
  const classStarted = resultsUnknown || hasScoringActivity;

  return (
    <Box
      data-testid="class-results"
      data-class-id={classId}
      data-final={final || undefined}
      data-revealed={revealed || undefined}
      sx={{
        mt: 1,
        mb: 1,
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: revealed ? 1.5 : 0 }}>
        <EmojiEventsIcon sx={{ color: colors.foxOrange }} />
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, color: "text.secondary" }}>
          {t("results.heading")}
        </Typography>
      </Stack>

      {!revealed ? (
        classStarted ? (
          <Typography data-testid="class-results-not-final" color="text.secondary">
            {t("results.notFinal")}
          </Typography>
        ) : (
          <Typography data-testid="class-results-not-started" color="text.secondary">
            {t("results.notStarted")}
          </Typography>
        )
      ) : divisionGroups ? (
        // #2634 — more than one division present: split into a heading per
        // group. Row markup (ResultRow) and row order within each group are
        // completely unchanged from the flat branch below, so AC7/AC9 (sort
        // order, PARTIAL anti-leak) hold without re-deriving anything here.
        <Stack spacing={2} data-testid="class-results-list" data-grouped="true">
          {divisionGroups.map((group) => (
            <Box key={group.token} data-testid="class-results-division-group" data-division-token={group.token}>
              <Typography
                data-testid="class-results-division-heading"
                data-division={group.label || t("results.divisionOpen")}
                variant="overline"
                // #2634 AC5 — the heading shows the division AS STORED, not
                // shouted. MUI's `overline` is one of the caseAllCaps variants
                // (@mui/material/styles/createTypography.js: `textTransform:
                // 'uppercase'`) and packages/brand does not override it, so
                // without this the DATA kept "Jr/YR" while the SCREEN read
                // "JR/YR" — the exact thing AC5 forbids by name. The variant is
                // kept for its size/weight/letter-spacing; only the transform
                // is dropped. Pinned by class-results-division-groups-
                // screenshots.cy.js, which reads the COMPUTED text-transform
                // (textContent is immune to the bug: CSS casing never reaches
                // the DOM string, which is why the original data-attribute
                // assertion could not fail).
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: "text.secondary",
                  display: "block",
                  mb: 0.5,
                  textTransform: "none",
                }}
              >
                {group.label || t("results.divisionOpen")}
              </Typography>
              <Stack spacing={1}>
                {group.rows.map((row) => (
                  <ResultRow key={row.rideId} row={row} t={t} />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack spacing={1} data-testid="class-results-list">
          {rows.map((row) => (
            <ResultRow key={row.rideId} row={row} t={t} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
